import { Alert, Badge, Button, Col, Form, Row, Spinner, Table } from "react-bootstrap";
import styles from "../../styles/Reports.module.css";
import ButtonOverride from "../../components/buttonoverride";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { useEffect, useMemo, useState } from "react";
import { useExportReportCsv, useRunReport } from "../../new/reports.query";
import useUserStore from "../../stores/user-store";
import { hasPermission } from "../../auth/permission-paths";

const buildInitialParameterValues = (parameters = []) => {
    return parameters.reduce((values, parameter) => {
        values[parameter.key] = parameter.defaultValue ?? (parameter.type === "boolean" ? "false" : "");
        return values;
    }, {});
};

const parseMultiselectValue = (value) => {
    if (!value || value === "all") {
        return ["all"];
    }

    return value
        .split(/[|,;]/)
        .map((item) => item.trim())
        .filter(Boolean);
};

const serializeMultiselectValue = (values) => {
    if (values.length === 0 || values.includes("all")) {
        return "all";
    }

    return `|${values.join("|")}|`;
};

const formatCellValue = (value, columnType) => {
    if (value === null || value === undefined) {
        return "";
    }

    if (columnType === "currency") {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: "USD"
        }).format(Number(value));
    }

    return value;
};

const downloadReportCsv = ({ blob, fileName }) => {
    const objectUrl = window.URL.createObjectURL(blob);
    const link = document.createElement("a");

    link.href = objectUrl;
    link.download = fileName;
    link.style.display = "none";
    document.body.appendChild(link);
    link.click();
    link.remove();

    window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0);
};

const ReportView = ({ report, returnFunction }) => {
    const orderedParameters = useMemo(() => {
        return [...(report.parameters ?? [])].sort((a, b) => a.order - b.order);
    }, [report.parameters]);
    const [parameterValues, setParameterValues] = useState(() => buildInitialParameterValues(orderedParameters));
    const [reportResult, setReportResult] = useState(null);
    const permissions = useUserStore((state) => state.permissions);
    const reportRun = useRunReport((data) => setReportResult(data));
    const reportExport = useExportReportCsv(downloadReportCsv);
    const columns = reportResult?.columns ?? [];
    const rows = reportResult?.rows ?? [];
    const canRunReport = hasPermission(permissions, report.runPermissionPath);

    useEffect(() => {
        setParameterValues(buildInitialParameterValues(orderedParameters));
        setReportResult(null);
    }, [orderedParameters, report.id]);

    const handleValueChange = (parameterKey, value) => {
        setParameterValues((currentValues) => ({
            ...currentValues,
            [parameterKey]: value
        }));
    };

    const handleRunReport = (event) => {
        event.preventDefault();
        reportExport.reset();
        reportRun.mutate({
            reportId: report.id,
            parameters: parameterValues
        });
    };

    const handleExportCsv = () => {
        if (!reportResult) {
            return;
        }

        reportExport.mutate({
            reportId: report.id,
            parameters: reportResult.parameters ?? {}
        });
    };

    return (
        <div className="clientCard">
            <div className="clientCardInner">
                <Row>
                    <Col xs={12}>
                        <div className={styles.reportViewHeader}>
                            <ButtonOverride
                                type="button"
                                className={styles.reportViewHeaderBackButton}
                                aria-label="Return"
                                onClick={returnFunction}
                            >
                                <FontAwesomeIcon icon={faCircleArrowLeft} size="2xl" />
                            </ButtonOverride>
                            <h4 className={styles.expenditureSubNavTitle}>
                                {report.title}
                            </h4>
                        </div>
                    </Col>
                    <Col xs={12} className="mt-3">
                        <div className={styles.reportDescriptionPanel}>
                            <strong>Report Description</strong>
                            <p>{report.description}</p>
                        </div>
                    </Col>
                    <Col xs={12} className="mt-3">
                        <Form className={styles.reportParameterPanel} onSubmit={handleRunReport}>
                            <div className={styles.reportParameterHeader}>
                                <strong>Parameters</strong>
                                <Badge bg="light" text="dark">{orderedParameters.length}</Badge>
                            </div>
                            <Row className="g-3">
                                {orderedParameters.map((parameter) => (
                                    <ReportParameterControl
                                        key={parameter.key}
                                        parameter={parameter}
                                        value={parameterValues[parameter.key] ?? ""}
                                        onChange={handleValueChange}
                                    />
                                ))}
                                {orderedParameters.length === 0 && (
                                    <Col xs={12}>
                                        <span className={styles.reportMutedText}>No parameters required.</span>
                                    </Col>
                                )}
                                <Col xs={12}>
                                    <div className="d-flex justify-content-end">
                                        <Button variant="primary" type="submit" disabled={reportRun.isPending || !canRunReport}>
                                            {reportRun.isPending && <Spinner animation="border" size="sm" className="me-2" />}
                                            Run Report
                                        </Button>
                                    </div>
                                </Col>
                            </Row>
                        </Form>
                    </Col>
                    <Col xs={12} className="mt-3">
                        {reportRun.isError && (
                            <Alert variant="danger">
                                The report could not be run.
                            </Alert>
                        )}
                        {reportExport.isError && (
                            <Alert variant="danger">
                                The report could not be exported as CSV.
                            </Alert>
                        )}
                        {reportExport.isSuccess && (
                            <Alert variant="success">
                                The CSV export is ready and the download has started.
                            </Alert>
                        )}
                        {reportResult && (
                            <div className={styles.reportResultHeader}>
                                <div>
                                    <h6 className="fw-bold mb-1">Report Data</h6>
                                    <span className={styles.reportMutedText}>
                                        Generated {new Date(reportResult.generatedAt).toLocaleString()}
                                    </span>
                                </div>
                                <div className={styles.reportResultActions}>
                                    <div className={styles.reportMetrics}>
                                        {(reportResult.summary?.metrics ?? []).map((metric) => (
                                            <span key={metric.label}>
                                                <strong>{metric.value}</strong> {metric.label}
                                            </span>
                                        ))}
                                    </div>
                                    <Button
                                        variant="outline-success"
                                        size="sm"
                                        type="button"
                                        onClick={handleExportCsv}
                                        disabled={reportExport.isPending || reportRun.isPending || !canRunReport}
                                    >
                                        {reportExport.isPending && <Spinner animation="border" size="sm" className="me-2" />}
                                        Export CSV
                                    </Button>
                                </div>
                            </div>
                        )}
                        {reportResult ? (
                            <div className={styles.reportTableWrap}>
                                <Table striped bordered hover responsive className="mb-0">
                                    <thead>
                                        <tr>
                                            {columns.map((column) => (
                                                <th key={column.key}>{column.header}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row, rowIndex) => (
                                            <tr key={rowIndex}>
                                                {columns.map((column) => (
                                                    <td key={column.key}>
                                                        {formatCellValue(row[column.key], column.type)}
                                                    </td>
                                                ))}
                                            </tr>
                                        ))}
                                        {rows.length === 0 && (
                                            <tr>
                                                <td colSpan={columns.length || 1}>
                                                    No rows returned.
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </Table>
                            </div>
                        ) : (
                            <div className={styles.reportEmptyState}>
                                Run the report to view data.
                            </div>
                        )}
                    </Col>
                </Row>
            </div>
        </div>
    );
};

const ReportParameterControl = ({ parameter, value, onChange }) => {
    const controlId = `report-param-${parameter.key}`;

    if (parameter.type === "multiselect") {
        const options = parameter.options ?? [];

        return (
            <Col xs={12} md={4}>
                <Form.Group controlId={controlId}>
                    <Form.Label>{parameter.label}</Form.Label>
                    <Form.Select
                        multiple
                        value={parseMultiselectValue(value)}
                        size={Math.min(Math.max(options.length, 4), 8)}
                        onChange={(event) => {
                            const selectedValues = Array.from(event.target.selectedOptions, (option) => option.value);
                            onChange(parameter.key, serializeMultiselectValue(selectedValues));
                        }}
                    >
                        {options.map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </Col>
        );
    }

    if (parameter.type === "boolean") {
        return (
            <Col xs={12} md={4}>
                <Form.Check
                    className={styles.reportBooleanField}
                    id={controlId}
                    type="checkbox"
                    label={parameter.label}
                    checked={value === "true"}
                    onChange={(event) => onChange(parameter.key, event.target.checked ? "true" : "false")}
                />
            </Col>
        );
    }

    if (parameter.type === "select") {
        return (
            <Col xs={12} md={4}>
                <Form.Group controlId={controlId}>
                    <Form.Label>{parameter.label}</Form.Label>
                    <Form.Select
                        required={parameter.required}
                        value={value}
                        onChange={(event) => onChange(parameter.key, event.target.value)}
                    >
                        {(parameter.options ?? []).map((option) => (
                            <option key={option.value} value={option.value}>
                                {option.label}
                            </option>
                        ))}
                    </Form.Select>
                </Form.Group>
            </Col>
        );
    }

    return (
        <Col xs={12} md={4}>
            <Form.Group controlId={controlId}>
                <Form.Label>{parameter.label}</Form.Label>
                <Form.Control
                    type={parameter.type === "number" ? "number" : parameter.type === "date" ? "date" : "text"}
                    required={parameter.required}
                    value={value}
                    onChange={(event) => onChange(parameter.key, event.target.value)}
                />
            </Form.Group>
        </Col>
    );
};

export default ReportView;
